import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const CopyrightPolicy = () => {
  return (
    <div className="min-h-screen bg-[#050810]" data-testid="copyright-policy-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/home">
            <Button variant="ghost" className="text-[#8A92A0] hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-6">
        <article className="max-w-3xl mx-auto">
          <header className="mb-12">
            <h1 className="font-['Unbounded'] text-4xl font-bold text-white mb-4">
              Copyright Policy
            </h1>
            <p className="text-[#8A92A0]">Last updated: January 2026</p>
          </header>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">1. Intellectual Property Rights</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                StardomFans.com respects the intellectual property rights of artists, creators, and content owners. 
                We expect all users to do the same. This policy outlines how we handle copyright matters on 
                our Platform and the protections we provide to rights holders.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">2. Artist Content Ownership</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                Artists who upload content to StardomFans.com retain full ownership of their intellectual property. 
                By uploading content, artists grant StardomFans.com a limited, non-exclusive license to host, display, 
                and distribute the content solely for the purpose of operating the Platform. This license does 
                not transfer ownership or provide StardomFans.com with any exclusive rights to the content.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">3. Content Upload Requirements</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                When uploading content to StardomFans.com, you represent and warrant that:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>You are the original creator or have obtained all necessary rights, licenses, and permissions</li>
                <li>Your content does not infringe on any third party's copyright, trademark, or other intellectual property rights</li>
                <li>You have obtained any required clearances for samples, covers, or derivative works</li>
                <li>You have obtained consent from all individuals appearing in your content</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">4. Copyright Notice and Takedown (Canada — Notice-and-Notice)</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                StardomFans.com operates under Canadian copyright law and follows the{" "}
                <strong className="text-white">Notice-and-Notice regime</strong> set out in sections 41.25 &amp; 41.26 of
                the <em>Copyright Act (Canada)</em>. If you believe your copyrighted work has been infringed on the
                Platform, you may submit a formal notice to our designated Copyright Agent via in-app messaging.
                Your notice must include:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Your full name and physical address</li>
                <li>Identification of the copyrighted work claimed to be infringed</li>
                <li>Identification of the alleged infringement (URL or on-Platform location)</li>
                <li>The date and time (with timezone) of the alleged infringement</li>
                <li>A statement of good-faith belief that the use is not authorized by the copyright owner, its agent, or the law of Canada</li>
                <li>A statement, made under the laws of British Columbia, that the information is accurate</li>
              </ul>
              <p className="text-[#8A92A0] leading-relaxed mt-3 text-sm">
                We will forward compliant notices to the user responsible for the content within a reasonable time
                and take other steps as required or permitted by law. Cross-border rights-holders may additionally
                submit US-style DMCA notices; we will treat them as equivalent notice-and-notice submissions.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">5. Counter-Notification</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                If you believe your content was wrongfully removed, you may submit a counter-notification including:
              </p>
              <ul className="list-disc list-inside text-[#8A92A0] space-y-2">
                <li>Your full name, physical address, and phone number</li>
                <li>Identification of the removed content and its former location</li>
                <li>A statement of good faith belief in wrongful removal, made under the laws of British Columbia</li>
                <li>Your consent to the exclusive jurisdiction of the courts of British Columbia, Canada</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">6. Repeat Infringer Policy</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                StardomFans.com maintains a policy of terminating accounts of users who are repeat copyright infringers
                under Canadian law. We track valid notices and may terminate accounts that receive multiple substantiated
                notices, regardless of whether counter-notifications are filed. This policy helps maintain the integrity
                of our Platform and protects the rights of content creators.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">7. Content Identification</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                StardomFans.com may employ content-identification technology to help detect potentially infringing
                uploads. However, this technology is not perfect, and rights-holders should still submit formal
                notice-and-notice submissions for confirmed infringements. We continuously work to improve our systems
                to better protect intellectual property.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">8. Licensing and Royalties</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                Artists are responsible for ensuring they have proper licenses for any content they upload, including
                mechanical licenses for cover songs, sample clearances, and synchronization licenses. StardomFans.com
                does not collect or distribute royalties on behalf of performing-rights organizations. Canadian artists
                should ensure they are properly registered with <strong className="text-white">SOCAN</strong> and{" "}
                <strong className="text-white">Re:Sound</strong> where applicable; international artists should register
                with their equivalent domestic PRO.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">9. Platform Content</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                The StardomFans.com name, logo, and Platform design are protected by copyright and trademark laws. 
                Users may not use StardomFans.com's intellectual property without prior written permission. This 
                includes the StardomFans.com brand, interface design, and any proprietary technology.
              </p>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">10. Contact Information</h2>
              <p className="text-[#8A92A0] leading-relaxed mb-4">
                For copyright inquiries or to submit a notice-and-notice submission, please contact our Copyright Agent via secure in-app messaging:
              </p>
              <div className="bg-[#0A0E18] rounded-lg p-6 text-[#8A92A0]">
                <p className="font-semibold text-white mb-2">StardomFans.com Copyright Agent — British Columbia, Canada</p>
                <p>
                  <Link to="/message-admin" className="text-[#EC008C] hover:underline" data-testid="copyright-contact-admin">
                    Message Admin (Copyright / Notice-and-Notice)
                  </Link>
                </p>
                <p>Subject Line: Copyright Notice — [Your Name]</p>
              </div>
            </section>

            <section>
              <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">11. Disclaimer</h2>
              <p className="text-[#8A92A0] leading-relaxed">
                This policy is provided for informational purposes and does not constitute legal advice. 
                If you have specific legal questions about copyright matters, we recommend consulting with 
                a qualified attorney. StardomFans.com reserves the right to modify this policy at any time.
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
            <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
            <Link to="/copyright" className="text-[#EC008C] text-sm">Copyright</Link>
            <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            <Link to="/privacy-report" className="text-[#00E68A] text-sm font-semibold">Privacy Report</Link>
          </div>
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default CopyrightPolicy;
